// Douăzeci de avatare, desenate aici, nu descărcate.
//
// Un set de imagini luate de pe internet ar aduce trei probleme deodată: licenţe pe care
// nu le putem verifica, fişiere de întreţinut şi o gazdă externă care într-o zi nu mai
// răspunde. Desenele astea sunt SVG generat din câteva reguli — câteva sute de octeţi
// fiecare, aceleaşi în orice temă, şi nu depind de nimeni.
//
// Fiecare are o formă şi o pereche de culori. Nu sunt „aleatorii": indicele alege exact
// acelaşi desen de fiecare dată, ca omul să-şi regăsească avatarul după ce l-a ales.

const NS = 'http://www.w3.org/2000/svg';

/** Perechile de culori. Fiecare e lizibilă pe fundal închis şi pe deschis deopotrivă. */
const PALETE = [
  ['#0F8BFD', '#0B4E8F'], ['#16C784', '#0B6B47'], ['#F6455F', '#8C1F30'],
  ['#F0B90B', '#8A6A06'], ['#965AFD', '#4E2C8F'], ['#00C2D7', '#046A76'],
  ['#FF7A45', '#8F3A18'], ['#4ADE80', '#166534'], ['#38BDF8', '#075985'],
  ['#E879F9', '#86198F'],
];

/** Cele două familii de forme, câte una pentru fiecare jumătate a setului. */
const FORME = ['cerc', 'unda'];

/**
 * Desenul cu indicele dat, ca element SVG.
 *
 * `marime` schimbă doar atributele; desenul e în coordonate 0–100, deci arată la fel
 * la 34 de pixeli în listă şi la 60 pe profil.
 */
export function avatarPreset(index, marime = 60) {
  const i = ((Number(index) || 0) % (PALETE.length * FORME.length) + PALETE.length * FORME.length)
    % (PALETE.length * FORME.length);
  const [prim, sec] = PALETE[i % PALETE.length];
  const forma = FORME[Math.floor(i / PALETE.length)];

  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('width', marime);
  svg.setAttribute('height', marime);
  svg.setAttribute('class', 'av-svg');

  const add = (tag, attrs) => {
    const n = document.createElementNS(NS, tag);
    for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
    svg.append(n);
    return n;
  };

  add('rect', { x: 0, y: 0, width: 100, height: 100, rx: 50, fill: sec });

  if (forma === 'cerc') {
    // Cercuri concentrice decalate — citeşte ca o ţintă, potrivit unui loc unde se
    // vorbeşte despre niveluri şi ţinte.
    add('circle', { cx: 50, cy: 50, r: 34, fill: 'none', stroke: prim, 'stroke-width': 9 });
    add('circle', { cx: 50, cy: 50, r: 14, fill: prim });
    add('circle', { cx: 72, cy: 30, r: 7, fill: prim, opacity: .75 });
  } else {
    // O linie de preţ stilizată: urcă, cade, urcă mai sus.
    add('path', {
      d: 'M14 66 L34 46 L48 58 L68 28 L86 40',
      fill: 'none', stroke: prim, 'stroke-width': 9,
      'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    });
    add('circle', { cx: 68, cy: 28, r: 8, fill: prim });
  }
  return svg;
}

export const NUMAR_PRESETURI = PALETE.length * FORME.length;

/**
 * Ce se pune într-un element de avatar: imaginea încărcată, desenul ales, sau iniţialele.
 *
 * Ordinea contează şi e mereu aceeaşi în toată aplicaţia — altfel acelaşi om ar arăta
 * diferit în chat şi pe profil.
 */
export function pictAvatar(gazda, user, marime = 60) {
  const val = user?.avatar || '';
  gazda.replaceChildren();
  gazda.classList.toggle('has-img', !!val);

  if (val.startsWith('preset:')) {
    gazda.append(avatarPreset(parseInt(val.slice(7), 10), marime));
    return;
  }
  // Fisier incarcat (`/media/…`) sau, pentru conturile de dinainte de mutare, imaginea
  // lipita in baza (`data:…`). Amandoua se pun la fel; deosebirea e doar de unde vin.
  if (val.startsWith('/media/') || val.startsWith('data:image/')) {
    const img = document.createElement('img');
    img.className = 'av-img';
    img.src = val;
    img.alt = user?.username || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    gazda.append(img);
    return;
  }
  gazda.textContent = (user?.username || '?').slice(0, 2).toUpperCase();
}



/**
 * Comprimă o imagine cât se poate fără să se vadă, ţintind o mărime de fişier.
 *
 * Un singur pas de compresie e o loterie: aceeaşi calitate dă 40 KB pe o captură de
 * grafic şi 600 KB pe o fotografie, fiindcă fotografia are mult mai multă informaţie.
 * De aceea coborâm calitatea în trepte până intrăm sub prag, şi abia dacă nici la
 * calitatea minimă nu încape, micşorăm şi laturile. Ordinea contează: ochiul iartă mai
 * uşor o compresie ceva mai dură decât o poză vizibil mai mică.
 *
 * WebP peste tot: la aceeaşi calitate percepută iese de două-trei ori mai mic decât
 * JPEG, iar browserele care nu-l ştiu au dispărut de mult.
 *
 * Pozele de avatar şi de copertă nu trec pe aici, ci prin `image-editor.js`, care le
 * taie întâi la cadrul ales de om. Asta rămâne pentru imaginile din publicaţii, unde
 * nu se taie nimic.
 */
export async function comprima(file, { laturaMax = 1600, tinta = 220 * 1024 } = {}) {
  const sursa = await incarcaImagine(file);
  let latura = Math.min(laturaMax, Math.max(sursa.width, sursa.height));

  for (let pas = 0; pas < 4; pas++) {
    for (const calitate of [0.82, 0.7, 0.58, 0.45]) {
      const url = deseneaza(sursa, latura, calitate);
      if (marimeDataUrl(url) <= tinta) return url;
    }
    latura = Math.round(latura * 0.75);   // nici la calitate mică n-a încăput
  }
  return deseneaza(sursa, latura, 0.45);
}

/** Câţi octeţi ocupă de fapt un `data:` URL, fără să-l decodăm. */
function marimeDataUrl(url) {
  const b64 = url.slice(url.indexOf(',') + 1);
  return Math.floor(b64.length * 3 / 4) - (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);
}

function incarcaImagine(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) { reject(new Error('Это не картинка')); return; }
    const cititor = new FileReader();
    cititor.onerror = () => reject(new Error('Не удалось прочитать файл'));
    cititor.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Не удалось открыть картинку'));
      img.onload = () => resolve(img);
      img.src = cititor.result;
    };
    cititor.readAsDataURL(file);
  });
}

/** Desenează imaginea încăpând în `latura`, păstrând proporţiile. */
function deseneaza(img, latura, calitate) {
  const scara = Math.min(1, latura / Math.max(img.width, img.height));
  const panza = document.createElement('canvas');
  panza.width = Math.max(1, Math.round(img.width * scara));
  panza.height = Math.max(1, Math.round(img.height * scara));
  const ctx = panza.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, panza.width, panza.height);
  return panza.toDataURL('image/webp', calitate);
}

// ------------------------------------------------------------------- coperti

/**
 * Benzile de sus ale profilului, desenate din gradiente.
 *
 * Nu sunt fotografii: o fotografie ar trebui gasita undeva, licentiata si servita, iar
 * trei sute de kiloocteti pentru un ornament din capul paginii e un pret prost. Astea
 * sunt cateva randuri de CSS, arata la fel pe orice ecran si nu cer nicio cerere de
 * retea. Cine vrea o fotografie o incarca — pentru asta e butonul de alaturi.
 *
 * Fiecare are doua straturi: o lumina colorata asezata intr-un colt si un fundal in
 * degrade. Stratul de sus e ce da senzatia de adancime; fara el ies benzi plate.
 */
export const COPERTI = [
  'radial-gradient(120% 180% at 12% 0%, rgba(15,139,253,.55), transparent 58%),'
  + ' linear-gradient(120deg, #10203a, #0b1220)',
  'radial-gradient(120% 180% at 88% 0%, rgba(22,199,132,.5), transparent 58%),'
  + ' linear-gradient(120deg, #0e2a22, #0b1220)',
  'radial-gradient(120% 180% at 20% 100%, rgba(246,69,95,.45), transparent 60%),'
  + ' linear-gradient(120deg, #2a0f18, #0b1220)',
  'radial-gradient(120% 180% at 78% 10%, rgba(150,90,253,.5), transparent 58%),'
  + ' linear-gradient(120deg, #1c1235, #0b1220)',
  'radial-gradient(130% 200% at 50% 0%, rgba(240,185,11,.42), transparent 62%),'
  + ' linear-gradient(120deg, #2a2110, #0b1220)',
  'radial-gradient(120% 180% at 10% 20%, rgba(0,194,215,.5), transparent 60%),'
  + ' linear-gradient(120deg, #0b2730, #0b1220)',
  'linear-gradient(120deg, rgba(15,139,253,.35), rgba(150,90,253,.3) 45%, transparent 75%),'
  + ' linear-gradient(120deg, #121a2e, #0b1220)',
  'linear-gradient(120deg, rgba(22,199,132,.32), rgba(0,194,215,.28) 50%, transparent 78%),'
  + ' linear-gradient(120deg, #0f2029, #0b1220)',
  'repeating-linear-gradient(115deg, rgba(255,255,255,.05) 0 2px, transparent 2px 22px),'
  + ' linear-gradient(120deg, #17203a, #0b1220)',
  'repeating-linear-gradient(65deg, rgba(15,139,253,.16) 0 3px, transparent 3px 26px),'
  + ' linear-gradient(120deg, #0e1830, #0b1220)',
  'radial-gradient(90% 140% at 95% 90%, rgba(255,122,69,.42), transparent 60%),'
  + ' linear-gradient(120deg, #2a1a12, #0b1220)',
  'radial-gradient(100% 160% at 50% 120%, rgba(232,121,249,.36), transparent 62%),'
  + ' linear-gradient(120deg, #241432, #0b1220)',
];

export const NUMAR_COPERTI = COPERTI.length;

/** Stilul de fundal pentru banda cu indicele dat. */
export function coperta(index) {
  const i = ((Number(index) || 0) % NUMAR_COPERTI + NUMAR_COPERTI) % NUMAR_COPERTI;
  return COPERTI[i];
}

/**
 * Pune coperta pe un element: desenul ales, poza incarcata, sau nimic.
 *
 * „Nimic" nu inseamna gol — inseamna banda in culoarea rangului, cea care exista de la
 * inceput. Un profil fara coperta trebuie sa arate ales, nu neterminat.
 */
export function pictCover(gazda, user, culoareRang = '#0F8BFD') {
  const val = user?.cover || '';
  if (val.startsWith('cover:')) {
    gazda.style.background = coperta(parseInt(val.slice(6), 10));
    gazda.style.backgroundSize = 'cover';
    return;
  }
  if (val.startsWith('/media/') || val.startsWith('data:image/')) {
    gazda.style.background = `center / cover no-repeat url("${val}")`;
    return;
  }
  gazda.style.background =
    `linear-gradient(120deg, ${culoareRang}44, transparent 62%), var(--bg-2)`;
}
